'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { BookOpen, Trash2 } from 'lucide-react';
import { MediaImage } from '@/shared/components/MediaImage';
import { Badge } from '@/shared/components/ui/badge';
import { LibraryTable, type LibraryColumn } from '@/shared/components/list';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import type { IllustrationStatus } from '@/contexts/illustrations/types';
import type { IllustrationSummary } from '../hooks/useBookLibrary';

function statusKey(s: IllustrationStatus): string {
  return ['ready', 'analyzing', 'generating', 'draft', 'failed'].includes(s) ? s : 'draft';
}
function statusVariant(s: IllustrationStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'ready') return 'default';
  if (s === 'analyzing' || s === 'generating') return 'secondary';
  if (s === 'failed') return 'destructive';
  return 'outline';
}

interface IllustrationTableViewProps {
  illustrations: IllustrationSummary[];
  storyName: (storyId: string) => string | undefined;
  onDelete: (id: string) => Promise<boolean> | void;
  onToggleFavorite: (id: string) => void;
}

export function IllustrationTableView({ illustrations, storyName, onDelete, onToggleFavorite }: IllustrationTableViewProps) {
  const t = useTranslations('illustrations.library');
  const tCard = useTranslations('illustrations.library.card');
  const tFav = useTranslations('illustrations.favorites');
  const locale = useLocale();
  const router = useRouter();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const illustrationUrl = (b: IllustrationSummary) =>
    `/${locale}/stories/${b.storyId}?book=${b.id}`;

  const columns: LibraryColumn<IllustrationSummary>[] = [
    {
      key: 'title',
      header: t('columns.title'),
      cell: (b) => (
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative h-10 w-8 shrink-0 overflow-hidden rounded bg-muted">
            {b.coverImageUrl ? (
              <MediaImage src={b.coverImageUrl} alt="" fill sizes="32px" className="object-cover" />
            ) : (
              <BookOpen className="absolute inset-0 m-auto h-4 w-4 text-muted-foreground/40" />
            )}
          </div>
          <span className="font-medium truncate">{b.title}</span>
        </div>
      ),
    },
    { key: 'story', header: t('columns.story'), hideOnMobile: true, cell: (b) => <span className="text-muted-foreground truncate">{storyName(b.storyId) ?? '—'}</span> },
    { key: 'language', header: t('columns.language'), hideOnMobile: true, cell: (b) => <span className="uppercase text-muted-foreground" data-testid={`illustration-language-${b.id}`}>{b.language ?? '—'}</span> },
    { key: 'status', header: t('columns.status'), cell: (b) => <Badge variant={statusVariant(b.status)} className="text-[10px]">{tCard(`status.${statusKey(b.status)}`)}</Badge> },
    { key: 'pages', header: t('columns.pages'), hideOnMobile: true, align: 'right', cell: (b) => <span className="tabular-nums text-muted-foreground">{b.generationProgress?.total ?? 0}</span> },
    { key: 'date', header: t('columns.createdAt'), hideOnMobile: true, cell: (b) => <span className="text-muted-foreground">{new Date(b.createdAt).toLocaleDateString(locale)}</span> },
  ];

  return (
    <>
      <LibraryTable
        testId="illustration-library-list"
        items={illustrations}
        columns={columns}
        getRowId={(b) => b.id}
        rowTestId={(b) => `illustration-library-item-${b.id}`}
        onRowClick={(b) => router.push(illustrationUrl(b))}
        favorite={{
          isFavorite: (b) => b.isFavorite,
          onToggle: (b) => onToggleFavorite(b.id),
          testId: (b) => `book-favorite-toggle-${b.id}`,
          label: (fav) => (fav ? tFav('removeFromFavorites') : tFav('addToFavorites')),
        }}
        actions={(b) => (
          <button
            onClick={(e) => { e.stopPropagation(); setDeleteId(b.id); }}
            className="p-2 flex items-center justify-center text-muted-foreground hover:text-destructive rounded"
            title={tCard('deleteDialog.title')}
            aria-label={tCard('deleteDialog.title')}
            data-testid={`illustration-delete-${b.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="illustration-library-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{tCard('deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{tCard('deleteDialog.description', { title: illustrations.find((b) => b.id === deleteId)?.title ?? '' })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="illustration-library-delete-cancel">{tCard('deleteDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="illustration-library-delete-confirm-button"
              onClick={() => { if (deleteId) onDelete(deleteId); setDeleteId(null); }}
            >
              {tCard('deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
